import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Button, Card, CardBody, Col, Collapse, Input, Row } from "reactstrap"
import ConsumptionTimeLine from "./CouponDetailsComponents/ConsumptionTimeLine"
import classNames from "classnames"
import ConsumptionAnalytic from "./CouponDetailsComponents/ConsumptionAnalytic"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getCoupon, updateCoupon } from "store/coupons/actions"
import { AddNewCoupon } from "./Modals"

// Keep original dummy data as fallback
const couponDetailsData = {
  id: "ID-145871",
  name: "Coupons Name",
  count: 10,
  price: "QAR 100",
  productSize: "Bottle 500ml",
  refillTimes: "3 Days/Week",
  refillSchedule: [
    { day: "sat", enabled: true, time: "earlyMorning" },
    { day: "sun", enabled: false, time: "notSelected" },
    { day: "mon", enabled: true, time: "afternoon" },
    { day: "tue", enabled: false, time: "notSelected" },
    { day: "wed", enabled: true, time: "night" },
    { day: "thu", enabled: false, time: "notSelected" },
    { day: "fri", enabled: false, time: "notSelected" },
  ],
  itemPerRefill: "1 Gallon/Once",
  status: "Active",
  createdAt: "2025-08-27",
  consumptionTimeLine: [
    {
      couponState: "Coupon Used",
      date: "23 dec, 2023 at 10:00 aM.",
      details: "A coupon pack for 10 refills was purchased and activated",
      location: "Home",
    },
    {
      couponState: "Coupon Used",
      date: "23 dec, 2023 at 10:00 aM.",
      details: "A coupon pack for 10 refills was purchased and activated",
      location: "Home",
    },
    {
      couponState: "Coupon Used",
      date: "23 dec, 2024 at 10:00 aM.",
      details: "2 bottles of 500ml water delivered. 3coupons remaining",
      location: "Work",
    },
    {
      couponState: "Coupon Used",
      date: "23 dec, 2024 at 10:00 aM.",
      details: "2 bottles of 500ml water delivered. 5 coupons remaining",
      location: "Work",
    },
    {
      couponState: "Coupon Activated",
      date: "23 dec, 2024 at 10:00 aM.",
      details: "A coupon pack for 10 refills was purchased and activated",
      location: "Home",
    },
  ],
  consumptionAnalytics: {
    total: 25,
    used: 10,
    remaining: 15,
    avgConsumption: "5 coupon / week",
    nextRedirectRefill: "23 dec, 2024",
    estCompletionDate: "23 dec, 2024",
    consmptionHabit:
      "Consumption is consistent, primarily on Friday. suggests a stable weekly routine",
    consumptionAddress: [
      {
        type: "Home",
        address: "123 Main St, Anytown, USA",
        default: true,
        count: 3,
      },
      {
        type: "Work",
        address: "456 Elm St, Anytown, USA",
        default: false,
        count: 2,
      },
    ],
  },
}

const CouponDetails = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id } = useParams<{ id: string }>()

  // Redux state
  const { selectedCoupon, loading } = useSelector((state: any) => state.Coupons)

  const [copied, setCopied] = useState(false)
  const [openFirstAccordion, setOpenFirstAccordion] = useState("1")
  const [openEditCouponModal, setOpenEditCouponModal] = useState(false)

  // Fetch coupon by ID on mount
  useEffect(() => {
    if (id) {
      dispatch(getCoupon(parseInt(id), navigate))
    }
  }, [id, dispatch, navigate])

  // Handle delete - inactivate coupon instead
  const handleDelete = () => {
    if (!selectedCoupon) {
      toast.error("Coupon not found")
      return
    }

    if (selectedCoupon.isActive === false) {
      toast.info("Coupon is already inactive")
      return
    }

      dispatch(updateCoupon(selectedCoupon.id, { isActive: false }, navigate))
  }

  // Map backend coupon data to UI display format
  // Uses "__" for missing fields as per requirements
  const mapBackendToUIData = () => {
    if (!selectedCoupon) {
      return couponDetailsData // Fallback to dummy data
    }

    return {
      id: selectedCoupon.id.toString(),
      name: selectedCoupon.enName || selectedCoupon.arName || "__",
      count: selectedCoupon.totalAvailableQuantity?.toString() || "__",
      price: selectedCoupon.discountValue?.toString() || "__",
      productSize: "__", // Not provided by backend
      refillTimes: "__", // Not provided by backend
      refillSchedule: couponDetailsData.refillSchedule, // Keep original dummy data
      itemPerRefill: "__", // Not provided by backend
      status: selectedCoupon.isExpired
        ? "In Active"
        : selectedCoupon.isActive
        ? "Active"
        : "In Active",
      createdAt: "__", // Not provided by backend
      // Keep original dummy consumption data
      consumptionTimeLine: couponDetailsData.consumptionTimeLine,
      consumptionAnalytics: couponDetailsData.consumptionAnalytics,
      // Backend specific fields for additional display
      code: selectedCoupon.code || "__",
      discountTypeName: selectedCoupon.discountTypeName || "__",
      expiryDate: selectedCoupon.expiryDate || "__",
      isExpired: selectedCoupon.isExpired,
      isActive: selectedCoupon.isActive,
    }
  }

  // Use mapped data if available, otherwise fallback to dummy data
  const displayData = mapBackendToUIData()

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <div className="page-content">
      <div className="container-fluid d-flex justify-content-between align-items-center">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("coupons_coupons")}
          subBreadcrumbItem={displayData.name}
        />
        <div className="d-flex gap-2">
          <Button
            onClick={() => {
              if (selectedCoupon) {
                setOpenEditCouponModal(true)
              }
            }}
            className="bg-gradiant-blue border-0 text-capitalize"
          >
            {t("coupons_editCoupon")}
          </Button>
          <Button
            onClick={handleDelete}
            className="bg-transparent border text-danger border-danger text-capitalize"
          >
            {t("coupons_deleteCoupon")}
          </Button>
        </div>
      </div>
      <Card className="text-center shadow-md rounded-3">
        <CardBody className="p-3">
          <div className="d-flex mb-2 align-items-start justify-content-between">
            <div className="d-flex flex-column align-items-start gap-1">
              <h4 className="fw-bold medium text-capitalize mb-0">
                {displayData.name}
              </h4>
              <span className="text-muted">
                {"code" in displayData && displayData.code && displayData.code !== "__"
                  ? `${t("coupons_code")}: ${displayData.code as string}`
                  : `${t("common_createdAt")}: ${displayData.createdAt}`}
              </span>
            </div>
          </div>

          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold "
            >
              {t("coupons_couponId")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted  m-0">{displayData.id}</p>
              <button
                className="relative cursor-pointer border-0 bg-transparent"
                onClick={() => {
                  copyToClipboard(displayData.id)
                }}
              >
                <i className="bx bx-copy px-2 text-primary bx-sm cursor-pointer"></i>
                {copied && (
                  <span
                    className="position-absolute  translate-middle-x mt-1 text-success small"
                    style={{ top: "14px", marginLeft: "20px" }}
                  >
                    {t("common_copied")}
                  </span>
                )}
              </button>
            </Col>
          </Row>
          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold "
            >
              {t("coupons_price")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted fw-semibold m-0">
               QAR {displayData.price}
              </p>
            </Col>
          </Row>
          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold"
            >
              {t("coupons_count")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted m-0">
                {displayData.count} {t("coupons_coupons")}
              </p>
            </Col>
          </Row>
          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold"
            >
              {t("coupons_productSize")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted m-0">{displayData.productSize}</p>
            </Col>
          </Row>
          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold"
            >
              {t("coupons_refillTimesPerWeek")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted m-0">{displayData.refillTimes}</p>
            </Col>
          </Row>
          {/* Refill Schedule Days */}
          <Row className="mt-2">
            <Col md={2}></Col>
            <Col md={10}>
              <div className="d-flex gap-2 flex-wrap">
                {displayData.refillSchedule.map((schedule) => (
                  <div
                    key={schedule.day}
                    className="border rounded p-2"
                    style={{ minWidth: "80px" }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="font-size-12 fw-semibold text-capitalize">
                        {t(`coupons_${schedule.day}`)}
                      </span>
                      <div className="form-check form-switch m-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={schedule.enabled}
                          disabled
                          style={{ cursor: "not-allowed" }}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <i className="mdi mdi-clock-outline font-size-12 text-muted"></i>
                      <span
                        className="font-size-10 text-muted"
                        style={{ opacity: schedule.enabled ? 1 : 0.5 }}
                      >
                        {schedule.enabled && schedule.time !== "notSelected"
                          ? t(`coupons_${schedule.time}`)
                          : t("coupons_notSelected")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
          <Row>
            <Col
              md={2}
              className="col-form-label text-start flex align-items-center fw-semibold"
            >
              {t("coupons_itemsCountPerOnce")}
            </Col>
            <Col
              md={10}
              className="d-flex align-items-center col-form-label text-start"
            >
              <p className="text-muted m-0">
                {displayData.itemPerRefill}
              </p>
            </Col>
          </Row>
          <div className="d-flex gap-2 align-items-center mt-2">
            <p className="text-muted text-start">
              <b>{t("coupons_compaignActive")}</b>
              <span> ({t("coupons_inactivePeriod")})</span>
            </p>
            <div className="form-check form-switch mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id={`customSwitch${displayData.id}`}
                checked={displayData.status === "Active"}
                onChange={() => {}}
              />
            </div>
          </div>
        </CardBody>
      </Card>
      <div className="accordion shadow-md rounded-3" id="accordion">
        <div className="accordion-item">
          <h2 className="accordion-header" id="headingOne">
            <button
              className={classNames("accordion-button", "fw-medium", {
                collapsed: !openFirstAccordion,
              })}
              type="button"
              onClick={() =>
                setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
              }
              style={{ cursor: "pointer" }}
            >
              {t("coupons_consumptionHistory")}
            </button>
          </h2>
          <Collapse
            isOpen={openFirstAccordion === "1"}
            className="accordion-collapse"
          >
            <div className="accordion-body">
              <Row>
                <Col lg="7">
                  <ConsumptionTimeLine
                    data={displayData.consumptionTimeLine}
                  />
                </Col>
                <Col lg="5">
                  <ConsumptionAnalytic
                    data={displayData.consumptionAnalytics}
                  />
                </Col>
              </Row>
            </div>
          </Collapse>
        </div>
      </div>

      {/* Edit Coupon Modal */}
      <AddNewCoupon
        isOpen={openEditCouponModal}
        toggle={() => setOpenEditCouponModal(false)}
        couponId={selectedCoupon?.id}
      />
    </div>
  )
}

export default CouponDetails
